import { useState, useEffect, useContext } from "react";
import {
  SDivider,
  SLink,
  SLinkContainer,
  SLinkIcon,
  SLinkIconBadge,
  SLinkLabel,
  SLinkNotification,
  SLogo,
  SSearch,
  SSearchIcon,
  SSidebar,
  SSidebarButton,
  SFooter,
} from "./styles";

import { logoWhiteSVG } from "../../assets";
import { clearPersistedGridState } from "../../helpers/useSessionState";
import packageJson from '../../../package.json';

// Import all needed icons
import {
  FaHome,
  FaBriefcase,
  FaClipboardList,
  FaCalculator,
  FaFileInvoice,
  FaFileSignature,
  FaCogs,
  FaClock,
  FaAngleDoubleLeft,
  FaCalendar,
  FaSearch,
  FaComment,
  FaWindowClose,
  FaPuzzlePiece,
  FaStar,
  FaGlobe,
  FaChartBar,
  FaRegStickyNote,
  FaAddressBook,
  FaCheckDouble,
  FaTshirt,
  FaClipboardCheck,
} from "react-icons/fa";

import { MdLogout } from "react-icons/md";
import { AuthContext } from "../../contexts/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import { getEnquiryCount } from "../../data/enquiryData";
import { getNoteCount } from "../../data/notes";
import Feedback from "../Modals/Feedback";
import { visibleManagementOptions } from "../../pages/Management/managementOptions";

const useEnquiryCount = () => {
  const [enquiryCount, setEnquiryCount] = useState(0);

  useEffect(() => {
    getEnquiryCount().then((count) => {
      setEnquiryCount(count);
    });
  }, []);

  return enquiryCount;
};

const useNotesCount = () => {
  const [notesCount, setNotesCount] = useState(0);

  useEffect(() => {
    const fetchNotesCount = async () => {
      try {
        const count = await getNoteCount();
        setNotesCount(count);
      } catch (error) {
        console.error("Error fetching notes count:", error);
        setNotesCount(0);
      }
    };

    fetchNotesCount();

    // Listen for notes updates
    const handleNotesUpdate = () => {
      fetchNotesCount();
    };

    window.addEventListener("notes:updated", handleNotesUpdate);
    window.addEventListener("storage", (e) => {
      if (e.key === "notesLastChanged") fetchNotesCount();
    });

    return () => {
      window.removeEventListener("notes:updated", handleNotesUpdate);
    };
  }, []);

  return notesCount;
};

const Sidebar = (props) => {
  const auth = useContext(AuthContext);
  const navigate = useNavigate();

  const enquiryCount = useEnquiryCount();
  const notesCount = useNotesCount();

  /**
   * Checks if the user has permission to access a specific route.
   */
  const canView = (name) => {
    const allowed = Array.isArray(props.allowedRoutes) ? props.allowedRoutes : [];
    return allowed.includes(name) || allowed.includes("Portal.Administrator");
  };

  const portalAccess = () => {
    const allowed = Array.isArray(props.allowedRoutes) ? props.allowedRoutes : [];
    return allowed.includes("Portal.Access");
  };

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const { pathname } = useLocation();

  const handleLogout = () => {
    window.localStorage.clear();
    auth.setLoggedIn(false);
    navigate("/");
  };

  // Handler to open the Feedback modal
  const openFeedbackModal = () => {
    setFeedbackModalOpen(true);
  };

  const closeFeedbackModal = () => {
    setFeedbackModalOpen(false);
  };

  // Filter links based on permissions
  const allFirstLinksArray = [
    {
      label: "Dashboard",
      icon: <FaHome />,
      to: "/",
      notification: 0,
      permission: null, // Dashboard available to all portal users
      // Going home is the app's "start again" action, so it throws away every
      // grid filter remembered for this session. Note this is a separate flag
      // from onClick, which turns a link into a non-navigational button.
      clearsFilters: true,
    },
    {
      label: "Notes",
      icon: <FaRegStickyNote />,
      to: "/notes?status=incomplete",
      notification: notesCount,
      permission: null, // Notes available to all portal users
    },
    {
      label: "Go/No-Go",
      icon: <FaCheckDouble />,
      to: "/go-no-go",
      notification: 0,
      permission: "GoNoGo.View",
    },
    {
      label: "Enquiries",
      icon: <FaClipboardList />,
      to: "/enquiries",
      notification: enquiryCount,
      permission: "Enquiries.View",
    },
    {
      label: "Quotes",
      icon: <FaCalculator />,
      to: "/quotes",
      notification: 0,
      permission: "Quotes.View",
    },
    {
      label: "Jobs",
      icon: <FaBriefcase />,
      to: "/jobs",
      notification: 0,
      permission: "Jobs.View",
    },
    {
      label: "Notifiable Works",
      icon: <FaClipboardCheck />,
      to: "/notifiable-works",
      notification: 0,
      permission: "NotifiableWorks.View",
    },
    {
      label: "Sales",
      icon: <FaFileInvoice />,
      to: "/invoicing",
      notification: 0,
      permission: "Sales.View",
    },
    {
      label: "Purchase Orders",
      icon: <FaFileSignature />,
      to: "/purchase-orders",
      notification: 0,
      permission: "PurchaseOrders.View",
    },
    {
      label: "Uniform",
      icon: <FaTshirt />,
      to: "/uniform",
      notification: 0,
      permission: "Uniform.Manage",
    },
    {
      label: "Service Reminders",
      icon: <FaClock />,
      to: "/service-reminders",
      notification: 0,
      permission: "ServiceReminders.View",
    },
    {
      label: "Feedback",
      icon: <FaComment />,
      onClick: openFeedbackModal,
      notification: 0,
      permission: null, // Feedback available to all portal users
    },
    {
      label: "Contacts",
      icon: <FaAddressBook />,
      to: "/contacts",
      notification: 0,
      permission: null, // Contacts available to all portal users
    },
  ];

  const firstlinksArray = allFirstLinksArray.filter(link => {
    const allowed = Array.isArray(props.allowedRoutes) ? props.allowedRoutes : [];
    
    // Special handling for Enquiries, Quotes, Jobs, and Invoices - require explicit permission, no admin bypass
    // Edit permission implies View permission
    if (link.label === "Enquiries") {
      const hasAccess = (allowed.includes("Enquiries.View") || allowed.includes("Enquiries.Edit")) && portalAccess();
      console.log("Enquiries check:", { hasEnquiriesView: allowed.includes("Enquiries.View"), hasEnquiriesEdit: allowed.includes("Enquiries.Edit"), hasPortalAccess: portalAccess(), hasAccess });
      return hasAccess;
    }
    if (link.label === "Quotes") {
      const hasAccess = (allowed.includes("Quotes.View") || allowed.includes("Quotes.Edit")) && portalAccess();
      console.log("Quotes check:", { hasQuotesView: allowed.includes("Quotes.View"), hasQuotesEdit: allowed.includes("Quotes.Edit"), hasPortalAccess: portalAccess(), hasAccess });
      return hasAccess;
    }
    if (link.label === "Jobs") {
      const hasAccess = (allowed.includes("Jobs.View") || allowed.includes("Jobs.Edit")) && portalAccess();
      console.log("Jobs check:", { hasJobsView: allowed.includes("Jobs.View"), hasJobsEdit: allowed.includes("Jobs.Edit"), hasPortalAccess: portalAccess(), hasAccess });
      return hasAccess;
    }
    if (link.label === "Sales") {
      const hasAccess = (allowed.includes("Sales.View") || allowed.includes("Sales.Edit")) && portalAccess();
      console.log("Sales check:", { hasSalesView: allowed.includes("Sales.View"), hasSalesEdit: allowed.includes("Sales.Edit"), hasPortalAccess: portalAccess(), hasAccess, allowedRoutes: allowed });
      return hasAccess;
    }
    if (link.label === "Purchase Orders") {
      // Edit implies view, and an administrator sees it either way.
      return (allowed.includes("PurchaseOrders.View")
        || allowed.includes("PurchaseOrders.Edit")
        || allowed.includes("Portal.Administrator")) && portalAccess();
    }
    if (link.label === "Notifiable Works") {
      // Its own role, and administrators.
      return (allowed.includes("NotifiableWorks.View")
        || allowed.includes("Portal.Administrator")) && portalAccess();
    }
    if (link.label === "Service Reminders") {
      return (allowed.includes("ServiceReminders.View") || allowed.includes("ServiceReminders.Edit")) && portalAccess();
    }
    if (link.label === "Go/No-Go") {
      return (allowed.includes("GoNoGo.View") || allowed.includes("GoNoGo.Edit")) && portalAccess();
    }
    
    // For all other links, use standard permission check
    return (!link.permission || canView(link.permission)) && portalAccess();
  });

  const allSecondLinksArray = [
    {
      label: "Diary",
      icon: <FaCalendar />,
      to: "/diary",
      notification: 0,
      permission: "Diary.View",
    },
    {
      label: "Pieces",
      icon: <FaPuzzlePiece />,
      to: "/pieces",
      notification: 0,
      permission: "Pieces.View",
    },
    {
      label: "Reports",
      icon: <FaChartBar />,
      to: "/reports",
      notification: 0,
      permission: "Reports.View",
    },
  ];

  const secondlinksArray = allSecondLinksArray.filter(link => {
    const allowed = Array.isArray(props.allowedRoutes) ? props.allowedRoutes : [];
    
    // Special handling for Diary - require explicit Diary.View or Diary.Edit permission
    if (link.label === "Diary") {
      const hasDiaryView = allowed.includes("Diary.View");
      const hasDiaryEdit = allowed.includes("Diary.Edit");
      const hasAccess = (hasDiaryView || hasDiaryEdit) && portalAccess();
      console.log('Diary permission check:', { hasDiaryView, hasDiaryEdit, hasAccess, allowedRoutes: allowed });
      return hasAccess;
    }
    
    // Special handling for Pieces - require explicit Pieces.View or Pieces.Edit permission
    if (link.label === "Pieces") {
      return (allowed.includes("Pieces.View") || allowed.includes("Pieces.Edit")) && portalAccess();
    }
    
    // Special handling for Reports - require explicit Reports.Pipeline or Reports.CompletedJobs permission
    if (link.label === "Reports") {
      return (allowed.includes("Reports.Pipeline") || allowed.includes("Reports.CompletedJobs")) && portalAccess();
    }
    
    // For all other links, use standard permission check
    return (!link.permission || canView(link.permission)) && portalAccess();
  });

  const allThirdLinksArray = [
    {
      label: "Management",
      icon: <FaCogs />,
      to: "/management",
      // Only for someone who can open at least one option on the page.
      visible: visibleManagementOptions(props.allowedRoutes).length > 0,
    },
    {
      label: "Logout",
      icon: <MdLogout />,
      onClick: handleLogout,
      permission: null, // Logout available to all users
    },
  ];

  const thirdLinksArray = allThirdLinksArray.filter(link => 
    link.visible !== false && (!link.permission || canView(link.permission))
  ).filter(link => portalAccess());

  const allFourthLinksArray = [
    {
      label: "Intranet",
      icon: <FaGlobe />,
      to: "https://bamfords.sharepoint.com/sites/intranet",
      notifications: 0,
      permission: null, // Intranet available to all portal users
      external: true, // Mark as external link
    },
    {
      label: "Global Search",
      icon: <FaSearch />,
      to: "/global-search",
      notifications: 0,
      permission: null, // Global Search available to all portal users
    },
  ];

  const fourthLinksArray = allFourthLinksArray.filter(link => 
    !link.permission || canView(link.permission)
  ).filter(link => portalAccess());

  const openSidebarHandler = () => {
    setSidebarOpen((p) => !p);
    props.onSidebarClick(sidebarOpen);
  };

  return (
    <>
      <SSidebarButton isOpen={sidebarOpen} onClick={openSidebarHandler}>
        <FaAngleDoubleLeft />
      </SSidebarButton>
      <SSidebar isOpen={sidebarOpen}>
        <div
          style={{
            width: "100%",
            display: "flex",
            // Expanded, the logo sits centred and flush to the top of the
            // section - the left padding is dropped too, since an offset would
            // pull it off centre. Collapsed keeps its original offset, where
            // the rail is only 100px wide and the logo needs the breathing room.
            justifyContent: sidebarOpen ? "center" : "flex-start",
            marginTop: sidebarOpen ? 0 : "2rem",
            paddingLeft: 0,
          }}
        >
          <SLogo isOpen={sidebarOpen}>
            <img src={logoWhiteSVG} alt="logo" />
          </SLogo>
        </div>

        <SDivider />

        {firstlinksArray.map(({ icon, label, notification, to, onClick, color, clearsFilters }) => {
          // Apply color directly to the icon if color is specified
          const styledIcon = color ? (
            <span style={{ color, backgroundColor: 'transparent !important', background: 'none !important' }}>
              {icon}
            </span>
          ) : icon;
          
          return (
          <SLinkContainer
            key={label}
            isActive={pathname === to}
            onClick={() => {
              if (clearsFilters) {
                clearPersistedGridState();
              }
              if (onClick) {
                onClick();
              }
            }}
          >
            {onClick ? (
              // Render as a non-navigational element if onClick is provided
              <SLink as="div" style={!sidebarOpen ? { width: `fit-content` } : {}}>
                <SLinkIcon style={label === "AI Assistant" ? { 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  padding: '8px'
                } : {}}>
                  {styledIcon}
                  {!sidebarOpen && !!notification && (
                    <SLinkIconBadge>{notification}</SLinkIconBadge>
                  )}
                </SLinkIcon>
                {sidebarOpen && (
                  <>
                    <SLinkLabel>{label}</SLinkLabel>
                    {!!notification && (
                      <SLinkNotification>{notification}</SLinkNotification>
                    )}
                  </>
                )}
              </SLink>
            ) : (
              <SLink to={to} style={!sidebarOpen ? { width: `fit-content` } : {}}>
                <SLinkIcon style={label === "AI Assistant" ? { 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  padding: '8px'
                } : {}}>
                  {styledIcon}
                  {!sidebarOpen && !!notification && (
                    <SLinkIconBadge>{notification}</SLinkIconBadge>
                  )}
                </SLinkIcon>
                {sidebarOpen && (
                  <>
                    <SLinkLabel>{label}</SLinkLabel>
                    {!!notification && (
                      <SLinkNotification>{notification}</SLinkNotification>
                    )}
                  </>
                )}
              </SLink>
            )}
          </SLinkContainer>
          );
        })}

        <SDivider />

        {fourthLinksArray.map(({ icon, label, notification, to, external }) => (
          <SLinkContainer key={label} isActive={pathname === to}>
            {external ? (
              <a 
                href={to} 
                target="_blank" 
                rel="noopener noreferrer"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  textDecoration: 'none',
                  color: 'inherit',
                  fontSize: '10pt',
                  padding: 'calc(8px - 2px) 0',
                  ...(sidebarOpen ? {} : { width: 'fit-content' })
                }}
              >
                <SLinkIcon>{icon}</SLinkIcon>
                {sidebarOpen && (
                  <>
                    <SLinkLabel>{label}</SLinkLabel>
                    {!!notification && (
                      <SLinkNotification>{notification}</SLinkNotification>
                    )}
                  </>
                )}
              </a>
            ) : (
              <SLink to={to} style={!sidebarOpen ? { width: `fit-content` } : {}}>
                <SLinkIcon>{icon}</SLinkIcon>
                {sidebarOpen && (
                  <>
                    <SLinkLabel>{label}</SLinkLabel>
                    {!!notification && (
                      <SLinkNotification>{notification}</SLinkNotification>
                    )}
                  </>
                )}
              </SLink>
            )}
          </SLinkContainer>
        ))}

        {secondlinksArray.map(({ icon, label, notification, to }) => (
          <SLinkContainer key={label} isActive={pathname === to}>
            <SLink to={to} style={!sidebarOpen ? { width: `fit-content` } : {}}>
              <SLinkIcon>{icon}</SLinkIcon>
              {sidebarOpen && (
                <>
                  <SLinkLabel>{label}</SLinkLabel>
                  {!!notification && (
                    <SLinkNotification>{notification}</SLinkNotification>
                  )}
                </>
              )}
            </SLink>
          </SLinkContainer>
        ))}

        <SDivider />

        {thirdLinksArray.map(({ icon, label, to, onClick }) => (
          <SLinkContainer
            key={label}
            isActive={pathname === to}
            onClick={onClick}
          >
            <SLink to={to} style={!sidebarOpen ? { width: `fit-content` } : {}}>
              <SLinkIcon>{icon}</SLinkIcon>
              {sidebarOpen && <SLinkLabel>{label}</SLinkLabel>}
            </SLink>
          </SLinkContainer>
        ))}

        <SDivider />
          
        v{packageJson.version}

      </SSidebar>

      {feedbackModalOpen && <Feedback onClose={closeFeedbackModal} />}
    </>
  );
};

export default Sidebar;
